import React from "react";

import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { CONTENT_MAX_WIDTH_STYLE } from "@/src/constants/size";

import SignupCustomInput from "./SignupCustomInput";
import SignupCustomUpload from "./SignupCustomUpload";
import { TypeEnum } from "../../../../api/ProjectAPI.types";
import SwitchOnOff from "../../../../components/common/tools/SwitchOnOff";
import "../../../../css/admin/platform.scss";
import BackgroundImageSection from "../section/BackgroundImageSection";

import type { JSX } from "react";

import type {
  Project,
  StorefrontProjectParams,
} from "../../../../api/ProjectAPI.types";

interface SignupPageEditorProps {
  project: Project;
  changeProject: (project: Project) => void;
  changeProjectParams: (
    key: keyof StorefrontProjectParams,
    value: StorefrontProjectParams[keyof StorefrontProjectParams],
  ) => void;
}

function SignupPageEditor({
  project,
  changeProject,
  changeProjectParams,
}: SignupPageEditorProps): JSX.Element | null {
  const { storefront_project_params: storefrontProjectParams } = project;
  const { t } = useTranslation("editor-platform");

  const history = useHistory();

  if (project.type === TypeEnum.Edu) {
    history.push("/layout");
    return null;
  }

  const isCustomField =
    storefrontProjectParams.display_user_custom_1 ||
    storefrontProjectParams.display_user_custom_2 ||
    storefrontProjectParams.display_user_custom_3;

  const classFormContainer = classNames("editor-form-section", {
    "section-separator": !isCustomField,
  });

  return (
    <div
      className="platform-configuration bg-card mx-auto max-w-(--maxWidth) rounded px-7.5 py-2.5 shadow"
      style={CONTENT_MAX_WIDTH_STYLE}
    >
      <div className="editor-form-signup-container">
        <div className={classFormContainer}>
          <div className="editor-form-section-title">
            {t("componentSignupType")}
          </div>
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={storefrontProjectParams.display_user_country}
                iconName=""
                name="display_user_country"
                size="sm"
                onChange={(value: boolean): void => {
                  changeProjectParams("display_user_country", value);
                }}
              />
            </div>
            <div className="editor-form-label">{t("show_countries")}</div>
          </div>
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={storefrontProjectParams.display_user_language}
                iconName=""
                name="display_user_language"
                size="sm"
                onChange={(value: boolean): void => {
                  changeProjectParams("display_user_language", value);
                }}
              />
            </div>
            <div className="editor-form-label">{t("show_languages")}</div>
          </div>
          {([1, 2, 3] as const).map((index) => (
            <SignupCustomInput
              key={`signup-custom-input-${index}`}
              changeProject={changeProject}
              changeProjectParams={changeProjectParams}
              index={index}
              project={project}
            />
          ))}
        </div>
        {isCustomField ? (
          <SignupCustomUpload
            changeProjectParams={changeProjectParams}
            project={project}
          />
        ) : null}
        <BackgroundImageSection component="signup" />
        <BackgroundImageSection component="signin" />
      </div>
    </div>
  );
}

export default SignupPageEditor;
